import { useRef, useEffect } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import { Box, Loader, Center, LoadingOverlay, useMantineColorScheme } from '@mantine/core';
import { detectMonacoLanguage } from './monacoUtils';

export interface MonacoCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  fileName?: string;
  onSave?: () => void;
  readOnly?: boolean;
  loading?: boolean;
  height?: string | number;
  theme?: 'vs-dark' | 'light';
}

export function MonacoCodeEditor({
  value,
  onChange,
  fileName,
  onSave,
  readOnly = false,
  loading = false,
  height = '100%',
  theme: customTheme,
}: MonacoCodeEditorProps) {
  const { colorScheme } = useMantineColorScheme();
  const onSaveRef = useRef(onSave);
  const editorRef = useRef<any>(null);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    try {
      // Register Ctrl+S / Cmd+S save command
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        if (onSaveRef.current) {
          onSaveRef.current();
        }
      });
    } catch {
      // Safe fallback if editor is disposed during command registration
    }
  };

  const language = detectMonacoLanguage(fileName);
  const editorTheme = customTheme || (colorScheme === 'dark' ? 'vs-dark' : 'light');

  return (
    <Box
      style={{
        width: '100%',
        height,
        position: 'relative',
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 1 }} />
      <Editor
        height="100%"
        language={language}
        value={value}
        path={fileName ? `inmemory://${fileName.replace(/^\/+/, '')}` : undefined}
        theme={editorTheme}
        onChange={(val) => onChange(val ?? '')}
        onMount={handleEditorDidMount}
        loading={
          <Center h="100%">
            <Loader size="sm" />
          </Center>
        }
        options={{
          readOnly,
          fontSize: 13,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          tabSize: 2,
          automaticLayout: true,
          lineNumbers: 'on',
          renderLineHighlight: 'all',
          formatOnPaste: true,
        }}
      />
    </Box>
  );
}
